import { BreadCrumb } from 'primereact/breadcrumb';
import { Toolbar } from 'primereact/toolbar';
import { FC, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { useNavigate, useParams } from 'react-router-dom';
import { getStudentAssignment } from '../graphQLOperations/Assignment';
import { AssignmentStudentAssignmentQuery } from '../graphQLOperations/__generated__/AssignmentStudentAssignmentQuery.graphql';
import { format } from 'date-fns';
import { AssignmentStudentCard } from '../components/Assignments/AssignmentStudentCard';
import { StudentSkillAssignment } from '../models/Assignment';
import { isPastDate } from '../businessLogicService/AssignmentsAux/isPastDate';

export const Assignment: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { assignment, studentSkillAssignments } = useLazyLoadQuery<AssignmentStudentAssignmentQuery>(
    getStudentAssignment,
    {
      skip: !id,
      assignmentId: `AssignmentNode:${id}`,
    },
    { fetchPolicy: 'network-only' },
  );
  useEffect(() => {
    const name = assignment?.name?.trim();
    document.title = name ? `Teach to One Roadmaps | Assignments | ${name}` : `Teach to One Roadmaps | Assignments`;
  }, [assignment?.name]);

  return (
    <div className="grid nc_content-frame nc_page-inner pb-8">
      <Toolbar className="col-10 col-offset-1 nc_page-header" center={<h1>Assignment from your teacher</h1>} />
      <BreadCrumb
        model={[{ label: 'Assignments', command: () => navigate('/assignments') }]}
        className="col-10 col-offset-1 nc_breadcrumb"
        home={{
          command: () => navigate('/'),
          template: () => (
            <span onClick={() => navigate('/')} className="p-menuitem-link cursor-pointer">
              <span className="nc_icon-nc_home icon-sm p-menuitem-icon" />
            </span>
          ),
        }}
        pt={{
          separator: {
            'aria-hidden': true,
          },
        }}
      />
      <div className="col-10 col-offset-1 grid nc_block">
        <div className="bg-white w-full border-round-xl p-4">
          <div className="flex justify-content-between">
            <h3 className="text-primary font-semibold">{assignment?.name}</h3>
            <div className="flex align-items-center">
              <div className="flex mr-5">
                <i className="nc_icon-nc_person icon-sm text-primary mr-2 font-semibold" />
                <span className="pt-1">Teacher: </span>
                <span className="font-semibold pt-1 ml-1">{`${assignment?.createdBy?.firstName} ${assignment?.createdBy?.lastName}`}</span>
              </div>
              {!isPastDate(assignment?.dueOn) ? (
                <>
                  <i className="nc_icon-nc_calendar icon-sm text-primary" />
                  <span className="ml-1">Due date: </span>
                  <span className="font-semibold ml-1">{format(new Date(assignment?.dueOn), 'P')}</span>
                </>
              ) : (
                <span className="py-2 px-3 font-semibold border-round bg-primary">Finished</span>
              )}
            </div>
          </div>
          <div className="mt-4">
            <p>
              {` To reach your goal, complete the tasks step by step. Start with a lesson or other resources, then take the
              "Skill Challenge" to show what you learned. Pass the challenge, and you'll get closer to your target. Keep
              going!`}
              🚀
            </p>
          </div>
          <div className="mt-4 p-5 grid gap-3 justify-content-center align-items-end w-full border-round-xl h-35rem background-ligth-gray">
            {([...(studentSkillAssignments?.nodes || [])] as StudentSkillAssignment[])
              .sort(a => (a.skill.id === assignment?.skill?.id ? 1 : 0))
              .map((ska, index, arr) => (
                <AssignmentStudentCard
                  isPast={isPastDate(assignment?.dueOn)}
                  key={ska?.id}
                  studentSkillAssignment={ska}
                  index={index}
                  lastIndex={arr.length - 1}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
